/* WEHLE-LIST-01 — section behaviours.
 *
 * The approved prototype ran each section inside a shadow root and executed its
 * script through `new Function(document, …)` against a proxied document. That
 * loader is prototype isolation and is deliberately NOT shipped. Here every
 * behaviour is bound to its own section element instead, so repeated instances
 * keep independent state, and each one is registered on Shopify's section
 * lifecycle events so the theme editor can load and unload it cleanly.
 *
 * Every init is idempotent: a second call on the same root is a no-op.
 */
(function () {
  'use strict';

  // Every lst01-block instance renders the same <script src>, which is the
  // theme's own pattern for section-scoped assets (the browser fetches the file
  // once). Browsers still EXECUTE one script element per tag, though, so on a
  // 20-section page this IIFE runs 20 times and would register 20 copies of the
  // section-lifecycle listeners below. Boot once.
  if (window.__lst01Booted) return;
  window.__lst01Booted = true;

  var REDUCED = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };
  var behaviour = function () { return REDUCED() ? 'instant' : 'smooth'; };

  /* ---- 1. inline card carousel ------------------------------------------
   * `.carousel-controls` immediately followed by `.cards`. Desktop lays the
   * cards out in a grid and the controls stay hidden; below 700px the track
   * scrolls horizontally and the controls activate. Ported from the shared
   * reference script, scoped to one section. */
  function initCardTracks(root) {
    root.querySelectorAll('.cards').forEach(function (track) {
      if (track.dataset.lstReady) return;
      var nav = track.previousElementSibling;
      if (!nav || !nav.matches('.carousel-controls')) return;
      track.dataset.lstReady = '1';

      var cards = Array.prototype.slice.call(track.children);
      var counter = nav.querySelector('.counter');
      var prev = nav.querySelector('[data-dir="-1"]');
      var next = nav.querySelector('[data-dir="1"]');
      if (!cards.length || !counter || !prev || !next) return;
      var idx = 0;

      function update() {
        var over = track.scrollWidth > track.clientWidth + 2;
        nav.classList.toggle('active', over);
        track.tabIndex = over ? 0 : -1;
        var base = track.getBoundingClientRect().left;
        idx = cards.reduce(function (best, c, i) {
          return Math.abs(c.getBoundingClientRect().left - base) <
            Math.abs(cards[best].getBoundingClientRect().left - base) ? i : best;
        }, 0);
        var end = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
        counter.textContent = (end && over ? cards.length : idx + 1) + ' / ' + cards.length;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = end;
      }
      function go(n) {
        n = Math.max(0, Math.min(cards.length - 1, n));
        track.scrollTo({ left: cards[n].offsetLeft - cards[0].offsetLeft, behavior: behaviour() });
      }
      nav.querySelectorAll('button').forEach(function (b) {
        b.addEventListener('click', function () { go(idx + Number(b.dataset.dir)); });
      });
      track.addEventListener('scroll', update, { passive: true });
      track.addEventListener('keydown', function (e) {
        if (['Home', 'End', 'ArrowLeft', 'ArrowRight'].indexOf(e.key) === -1) return;
        e.preventDefault();
        go(e.key === 'Home' ? 0 : e.key === 'End' ? cards.length - 1 : idx + (e.key === 'ArrowLeft' ? -1 : 1));
      });
      var ro = new ResizeObserver(update);
      ro.observe(track);
      track._lstDispose = function () { ro.disconnect(); };
      update();
    });
  }

  /* ---- 2. `ul.track` carousel with labelled Zurück / Weiter buttons -------
   * Used by the review slots and the nine audience-fit cards. The controls are
   * found structurally (not by id) so both instances share one implementation
   * and neither can capture the other's buttons. */
  function initTracks(root) {
    root.querySelectorAll('ul.track').forEach(function (track) {
      if (track.dataset.lstReady) return;
      var nav = root.querySelector('.navigation');
      if (!nav) return;
      var prev = nav.querySelector('.buttons button:not(.primary)');
      var next = nav.querySelector('.buttons button.primary');
      var position = nav.querySelector('span');
      var announce = root.querySelector('[aria-live].lst-sr-only, [aria-live].sr-only');
      var cards = Array.prototype.slice.call(track.children);
      if (!prev || !next || !position || !cards.length) return;
      track.dataset.lstReady = '1';
      var index = 0;

      function update() {
        var overflow = track.scrollWidth > track.clientWidth + 2;
        nav.hidden = !overflow;
        track.tabIndex = overflow ? 0 : -1;
        var edge = track.getBoundingClientRect().left;
        index = cards.reduce(function (best, c, i) {
          return Math.abs(c.getBoundingClientRect().left - edge) <
            Math.abs(cards[best].getBoundingClientRect().left - edge) ? i : best;
        }, 0);
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
        position.textContent = (next.disabled && overflow ? cards.length : index + 1) + ' / ' + cards.length;
      }
      function go(n) {
        n = Math.max(0, Math.min(cards.length - 1, n));
        track.scrollTo({ left: cards[n].offsetLeft - cards[0].offsetLeft, behavior: behaviour() });
        if (announce) {
          announce.textContent = (cards[n].getAttribute('aria-label') || '') + ', ' + (n + 1) + ' von ' + cards.length;
        }
      }
      prev.addEventListener('click', function () { go(index - 1); });
      next.addEventListener('click', function () { go(index + 1); });
      track.addEventListener('scroll', update, { passive: true });
      track.addEventListener('keydown', function (e) {
        if (['ArrowRight', 'ArrowLeft', 'Home', 'End'].indexOf(e.key) === -1) return;
        e.preventDefault();
        go(e.key === 'Home' ? 0 : e.key === 'End' ? cards.length - 1 : index + (e.key === 'ArrowRight' ? 1 : -1));
      });
      var onResize = function () {
        if (window.matchMedia('(min-width:701px)').matches) track.scrollLeft = 0;
        update();
      };
      window.addEventListener('resize', onResize);
      track._lstDispose = function () { window.removeEventListener('resize', onResize); };
      update();
    });
  }

  /* ---- 3. laboratory-report dialog --------------------------------------
   * Native <dialog>.showModal() supplies the focus trap and Escape handling;
   * we add scroll containment on the page behind it and focus restoration. */
  function initDialog(root) {
    var trigger = root.querySelector('.report-trigger');
    var dialog = root.querySelector('dialog');
    if (!trigger || !dialog || trigger.dataset.lstReady) return;
    trigger.dataset.lstReady = '1';
    var previousOverflow = '';

    trigger.addEventListener('click', function () {
      previousOverflow = document.body.style.overflow;
      dialog.showModal();
      document.body.style.overflow = 'hidden';
      var scroller = dialog.querySelector('.dialog-scroll');
      if (scroller) scroller.scrollTop = 0;
    });
    var close = dialog.querySelector('.dialog-close');
    if (close) close.addEventListener('click', function () { dialog.close(); });
    dialog.addEventListener('close', function () {
      document.body.style.overflow = previousOverflow;
      trigger.focus({ preventScroll: true });
    });
    // backdrop click: only when the pointer is genuinely outside the panel
    dialog.addEventListener('click', function (e) {
      if (e.target !== dialog) return;
      var r = dialog.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close();
    });
  }

  /* ---- 4. golden-spoon connector overlay --------------------------------
   * Draws the leader lines from the ring edge to each floating amount card and
   * places the percentage badge on the line. Geometry is the approved one. */
  function initSpoon(root) {
    var stage = root.querySelector('.spoon-stage');
    if (!stage || stage.dataset.lstReady) return;
    stage.dataset.lstReady = '1';
    var overlay = stage.querySelector('.connector-overlay');
    if (!overlay) return;
    var ANGLES = { collagen: 35, glucosamine: 174, msm: 211, 'vitamin-c': 223.61 };

    function update() {
      var mobile = window.matchMedia('(max-width:1023px)').matches;
      var svg = stage.querySelector(mobile ? '.chart-mobile' : '.chart-desktop');
      if (!svg) return;
      var matrix = svg.getScreenCTM();
      if (!matrix) return;
      var box = stage.getBoundingClientRect();
      overlay.setAttribute('viewBox', '0 0 ' + box.width + ' ' + box.height);
      var cx = mobile ? 155 : 620, cy = mobile ? 250 : 390, r = mobile ? 133 : 190;

      stage.querySelectorAll('.ingredient-connector').forEach(function (g) {
        var key = g.dataset.ingredient;
        var cardEl = stage.querySelector('.ingredient-' + key);
        if (!cardEl) return;
        var card = cardEl.getBoundingClientRect();
        var a = ANGLES[key] * Math.PI / 180;
        var pt = new DOMPoint(cx + r * Math.cos(a), cy + r * Math.sin(a)).matrixTransform(matrix);
        var x = pt.x - box.left, y = pt.y - box.top, tx, ty, d, lx, ly;

        if (!mobile) {
          var right = key === 'collagen' || key === 'vitamin-c';
          tx = (right ? card.left : card.right) - box.left;
          ty = card.top - box.top + Math.min(card.height / 2, 64);
          if (key === 'vitamin-c') {
            var elbowX = x + 18;
            d = 'M ' + x + ' ' + y + ' L ' + elbowX + ' ' + ty + ' L ' + tx + ' ' + ty;
            lx = (elbowX + tx) / 2; ly = ty;
          } else {
            d = 'M ' + x + ' ' + y + ' L ' + tx + ' ' + ty;
            lx = (x + tx) / 2; ly = (y + ty) / 2;
          }
        } else if (key === 'msm' || key === 'vitamin-c') {
          tx = card.left - box.left + card.width / 2;
          ty = card.bottom - box.top;
          var elbowY = ty + (key === 'msm' ? 24 : 48);
          d = 'M ' + x + ' ' + y + ' L ' + x + ' ' + elbowY + ' L ' + tx + ' ' + elbowY + ' L ' + tx + ' ' + ty;
          lx = (x + tx) / 2; ly = elbowY;
        } else if (key === 'glucosamine') {
          tx = card.left - box.left + Math.min(card.width * 0.3, 104);
          ty = card.top - box.top;
          var ex = Math.max(-8, x - 16), ey = ty - 12;
          d = 'M ' + x + ' ' + y + ' L ' + ex + ' ' + y + ' L ' + ex + ' ' + ey + ' L ' + tx + ' ' + ey + ' L ' + tx + ' ' + ty;
          lx = (ex + tx) / 2; ly = ey;
        } else {
          tx = card.left - box.left + card.width * 0.7;
          ty = card.top - box.top;
          d = 'M ' + x + ' ' + y + ' L ' + tx + ' ' + ty;
          lx = (x + tx) / 2; ly = (y + ty) / 2;
        }

        g.querySelector('path').setAttribute('d', d);
        var badge = g.querySelector('.connector-badge');
        badge.setAttribute('transform', 'translate(' + lx + ' ' + ly + ')');
        var text = g.querySelector('text');
        text.setAttribute('x', '0');
        text.setAttribute('y', '0');
        var width = text.getComputedTextLength() + 12;
        var rect = g.querySelector('rect');
        rect.setAttribute('x', -width / 2);
        rect.setAttribute('y', -12);
        rect.setAttribute('width', width);
        rect.setAttribute('height', 24);
      });
    }

    var scheduled = false;
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () { scheduled = false; update(); });
    }
    var ro = new ResizeObserver(schedule);
    ro.observe(stage);
    stage.querySelectorAll('.ingredient-row').forEach(function (e) { ro.observe(e); });
    window.addEventListener('resize', schedule);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
    stage._lstDispose = function () {
      ro.disconnect();
      window.removeEventListener('resize', schedule);
    };
    schedule();
  }

  function init(root) {
    if (!root) return;
    initCardTracks(root);
    initTracks(root);
    initDialog(root);
    initSpoon(root);
  }

  function dispose(root) {
    if (!root) return;
    root.querySelectorAll('.cards, ul.track, .spoon-stage').forEach(function (el) {
      if (typeof el._lstDispose === 'function') el._lstDispose();
      delete el.dataset.lstReady;
    });
    var trigger = root.querySelector('.report-trigger');
    if (trigger) delete trigger.dataset.lstReady;
  }

  function boot() { document.querySelectorAll('.lst01').forEach(init); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme-editor lifecycle
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('.lst01') : null;
    if (root) init(root);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('.lst01') : null;
    if (root) dispose(root);
  });
})();
